import { FC, useState, useEffect, useRef } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { adminService } from '@/services/admin.service';
import { videoService } from '@/services/video.service';
import { videoUploadErrorMessage } from '@/utils/videoUploadError';
import { useToast } from '@/contexts/ToastContext';
import { useAuthContext } from '@/contexts/AuthContext';
import VideoUploadPanel from '@/components/admin/VideoUploadPanel';
import type { AttachedVideo } from '@/services/videoUpload/types';
import type { AdminLesson, AdminLessonQuiz, AdminQuizQuestion, AdminQuizAnswer } from '@/types/admin';

interface AdminLessonEditorProps {
  trailId: string;
  lesson: AdminLesson;
  onSave: (lesson: AdminLesson) => void;
  onCancel: () => void;
  /** O commit do upload direto grava `Lesson.content` no SERVIDOR, fora do
   *  fluxo do Salvar. Sem avisar o pai, a lista continuaria com a versão
   *  antiga e um Cancelar depois do envio faria o vídeo "sumir" da tela. */
  onLessonMutated?: (lesson: AdminLesson) => void;
}

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[\w-]+/;

const emptyAnswer = (order: number): AdminQuizAnswer => ({
  text: '',
  is_correct: false,
  order,
});

const emptyQuestion = (order: number): AdminQuizQuestion => ({
  text: '',
  explanation: '',
  order,
  answers: [emptyAnswer(0), emptyAnswer(1), emptyAnswer(2), emptyAnswer(3)],
});

type VideoType = 'none' | 'own' | 'youtube';

// Teto do proxy multipart (backend: MAX_VIDEO_SIZE em video_upload_views.py).
// É o piso: com o upload direto ligado, o teto real vem do backend em
// `/api/auth/me/` e pode ser muito maior (GiB).
const PROXY_MAX_VIDEO_BYTES = 500 * 1024 * 1024;

const AdminLessonEditor: FC<AdminLessonEditorProps> = ({
  trailId,
  lesson,
  onSave,
  onCancel,
  onLessonMutated,
}) => {
  const { showToast } = useToast();
  const { user } = useAuthContext();
  const directUploadEnabled = user?.video_direct_upload_enabled === true;
  const maxVideoBytes = directUploadEnabled
    ? (user?.video_max_size_bytes ?? PROXY_MAX_VIDEO_BYTES)
    : PROXY_MAX_VIDEO_BYTES;
  const [title, setTitle] = useState(lesson.title);
  const [markdown, setMarkdown] = useState(lesson.content?.markdown || '');
  const [youtubeUrl, setYoutubeUrl] = useState(lesson.content?.youtube_url || '');
  const [attachments, setAttachments] = useState(lesson.content?.attachments || []);
  const [minTimeSeconds, setMinTimeSeconds] = useState(lesson.min_time_seconds);
  const [durationMinutes, setDurationMinutes] = useState(lesson.duration_minutes);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [youtubeError, setYoutubeError] = useState('');

  // Video state
  const initialVideoType: VideoType = lesson.content?.video_blob_name
    ? 'own'
    : lesson.content?.youtube_url
      ? 'youtube'
      : 'none';
  const [videoType, setVideoType] = useState<VideoType>(initialVideoType);
  const [videoBlobName, setVideoBlobName] = useState(lesson.content?.video_blob_name || '');
  const [videoFilename, setVideoFilename] = useState(lesson.content?.video_filename || '');
  const [videoSizeBytes, setVideoSizeBytes] = useState(lesson.content?.video_size_bytes || 0);
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState(lesson.content?.video_thumbnail_url || '');
  const [videoTitle, setVideoTitle] = useState(lesson.content?.video_title || '');
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Quiz state
  const [quizEnabled, setQuizEnabled] = useState(false);
  const [quizTitle, setQuizTitle] = useState('');
  const [minCorrectAnswers, setMinCorrectAnswers] = useState(1);
  const [questions, setQuestions] = useState<AdminQuizQuestion[]>([]);
  const [quizExistsOnServer, setQuizExistsOnServer] = useState(false);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);

  // Load existing quiz
  useEffect(() => {
    const loadQuiz = async () => {
      setIsLoadingQuiz(true);
      try {
        const quiz = await adminService.getLessonQuiz(lesson.id);
        if (quiz) {
          setQuizEnabled(quiz.is_active);
          setQuizTitle(quiz.title);
          setMinCorrectAnswers(quiz.min_correct_answers);
          setQuestions(quiz.questions);
          setQuizExistsOnServer(true);
        }
      } catch { /* no quiz */ }
      setIsLoadingQuiz(false);
    };
    loadQuiz();
  }, [lesson.id]);

  const handleYoutubeChange = (value: string) => {
    setYoutubeUrl(value);
    if (value && !YOUTUBE_REGEX.test(value)) {
      setYoutubeError('URL do YouTube inválida');
    } else {
      setYoutubeError('');
    }
  };

  const getYoutubeEmbedUrl = (url: string): string | null => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await adminService.uploadFile(file);
      setAttachments((prev) => [...prev, { name: result.name, url: result.url, size_bytes: result.size_bytes }]);
    } catch {
      showToast('Erro ao fazer upload', 'error');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Quiz question handlers
  const addQuestion = () => {
    setQuestions((prev) => [...prev, emptyQuestion(prev.length)]);
  };

  const removeQuestion = (qIdx: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== qIdx).map((q, i) => ({ ...q, order: i })));
  };

  const updateQuestionText = (qIdx: number, text: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === qIdx ? { ...q, text } : q)));
  };

  const updateQuestionExplanation = (qIdx: number, explanation: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === qIdx ? { ...q, explanation } : q)));
  };

  const addAnswer = (qIdx: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx ? { ...q, answers: [...q.answers, emptyAnswer(q.answers.length)] } : q
      )
    );
  };

  const removeAnswer = (qIdx: number, aIdx: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx
          ? { ...q, answers: q.answers.filter((_, j) => j !== aIdx).map((a, j) => ({ ...a, order: j })) }
          : q
      )
    );
  };

  const updateAnswerText = (qIdx: number, aIdx: number, text: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx
          ? { ...q, answers: q.answers.map((a, j) => (j === aIdx ? { ...a, text } : a)) }
          : q
      )
    );
  };

  const setCorrectAnswer = (qIdx: number, aIdx: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx
          ? { ...q, answers: q.answers.map((a, j) => ({ ...a, is_correct: j === aIdx })) }
          : q
      )
    );
  };

  // Video handlers — a mecânica de envio vive no VideoUploadPanel; aqui só
  // reagimos ao resultado.
  const handleVideoUploaded = (video: AttachedVideo, mutatedLesson: unknown | null) => {
    setVideoBlobName(video.blob_name);
    setVideoFilename(video.filename);
    setVideoSizeBytes(video.size_bytes);
    showToast('Vídeo enviado com sucesso!', 'success');
    // No caminho direto o servidor JÁ gravou o vídeo em `Lesson.content`. Se o
    // pai não souber disso, sua cópia da lição fica velha e um Salvar posterior
    // reenviaria o conteúdo antigo por cima.
    if (mutatedLesson) onLessonMutated?.(mutatedLesson as AdminLesson);
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingThumbnail(true);
    try {
      const result = await videoService.uploadVideoThumbnail(file);
      setVideoThumbnailUrl(result.url);
      showToast('Thumbnail enviada!', 'success');
    } catch {
      showToast('Erro ao enviar thumbnail', 'error');
    } finally {
      setIsUploadingThumbnail(false);
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
    }
  };

  // Os 5 setters de vídeo eram repetidos em 3 lugares; um deles esquecia um campo.
  const clearVideoState = () => {
    setVideoBlobName('');
    setVideoFilename('');
    setVideoSizeBytes(0);
    setVideoThumbnailUrl('');
    setVideoTitle('');
  };

  const handleRemoveVideo = async () => {
    if (!videoBlobName) return;
    const confirmed = window.confirm('Tem certeza que deseja remover o vídeo? O arquivo será deletado do servidor.');
    if (!confirmed) return;

    try {
      await videoService.deleteVideo(lesson.id);
      clearVideoState();
      showToast('Vídeo removido', 'success');
    } catch (err) {
      // 404 = a lição não tem esse vídeo persistido (foi enviado e ainda não
      // salvo). Antes a UI travava exibindo um vídeo que a lição não tem;
      // limpar o state é a única saída correta.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        clearVideoState();
        showToast('Vídeo removido', 'success');
        return;
      }
      showToast('Erro ao remover vídeo', 'error');
    }
  };

  const handleVideoTypeChange = (type: VideoType) => {
    if (videoType === 'own' && type !== 'own' && videoBlobName) {
      const confirmed = window.confirm(
        'Ao mudar o tipo de vídeo, o vídeo atual será deletado do servidor. Continuar?'
      );
      if (!confirmed) return;
      videoService.deleteVideo(lesson.id).catch(() => {});
      clearVideoState();
    }
    setVideoType(type);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSave = async () => {
    if (!title.trim()) {
      showToast('Título é obrigatório', 'warning');
      return;
    }

    // Validate quiz if enabled
    if (quizEnabled && questions.length > 0) {
      for (let i = 0; i < questions.length; i++) {
        if (!questions[i].text.trim()) {
          showToast(`Pergunta ${i + 1} está sem texto`, 'warning');
          return;
        }
        const hasCorrect = questions[i].answers.some((a) => a.is_correct);
        if (!hasCorrect) {
          showToast(`Pergunta ${i + 1} não tem resposta correta marcada`, 'warning');
          return;
        }
        const filledAnswers = questions[i].answers.filter((a) => a.text.trim());
        if (filledAnswers.length < 2) {
          showToast(`Pergunta ${i + 1} precisa de pelo menos 2 alternativas`, 'warning');
          return;
        }
      }
      if (minCorrectAnswers > questions.length) {
        showToast('Mínimo de acertos não pode ser maior que o número de perguntas', 'warning');
        return;
      }
    }

    setIsSaving(true);
    try {
      // Build content with video fields
      const content: AdminLesson['content'] = {
        markdown,
        youtube_url: videoType === 'youtube' ? (youtubeUrl || null) : null,
        attachments,
        video_blob_name: videoType === 'own' && videoBlobName ? videoBlobName : null,
        video_filename: videoType === 'own' && videoBlobName ? videoFilename : null,
        video_size_bytes: videoType === 'own' && videoBlobName ? videoSizeBytes : null,
        video_thumbnail_url: videoType === 'own' && videoBlobName ? (videoThumbnailUrl || null) : null,
        video_title: videoType === 'own' && videoBlobName ? (videoTitle || null) : null,
      };

      // Save lesson
      const updated = await adminService.updateLesson(trailId, lesson.id, {
        title,
        content,
        min_time_seconds: minTimeSeconds,
        duration_minutes: durationMinutes,
      });

      // Save quiz
      if (quizEnabled && questions.length > 0) {
        const cleanQuestions = questions.map((q, qi) => ({
          ...q,
          order: qi,
          answers: q.answers.filter((a) => a.text.trim()).map((a, ai) => ({ ...a, order: ai })),
        }));
        const quizPayload: Omit<AdminLessonQuiz, 'id' | 'lesson_id'> = {
          title: quizTitle,
          min_correct_answers: minCorrectAnswers,
          is_active: true,
          questions: cleanQuestions,
        };
        if (quizExistsOnServer) {
          await adminService.updateLessonQuiz(lesson.id, quizPayload);
        } else {
          await adminService.createLessonQuiz(lesson.id, quizPayload);
          setQuizExistsOnServer(true);
        }
      } else if (!quizEnabled && quizExistsOnServer) {
        await adminService.deleteLessonQuiz(lesson.id);
        setQuizExistsOnServer(false);
      }

      onSave(updated);
    } catch {
      showToast('Erro ao salvar página', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const embedUrl = getYoutubeEmbedUrl(youtubeUrl);

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="lesson-editor">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={onCancel} className="text-content-muted hover:text-content-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-2xl font-bold">Editar Página</h1>
      </div>

      <div className="space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1">Título da Página *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
          />
        </div>

        {/* Markdown Editor */}
        <div data-color-mode="dark">
          <label className="block text-sm font-medium mb-1">Conteúdo (Markdown)</label>
          <MDEditor
            value={markdown}
            onChange={(val) => setMarkdown(val || '')}
            height={400}
            preview="live"
          />
        </div>

        {/* Video Section */}
        <div className="border border-theme-border rounded-xl p-4 space-y-4" data-testid="video-section">
          <label className="block text-sm font-medium">Vídeo da Página</label>

          {/* Radio buttons */}
          <div className="flex gap-6">
            {(['none', 'own', 'youtube'] as VideoType[]).map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="videoType"
                  checked={videoType === type}
                  onChange={() => handleVideoTypeChange(type)}
                  className="accent-accent"
                />
                {type === 'none' && 'Nenhum vídeo'}
                {type === 'own' && 'Vídeo próprio'}
                {type === 'youtube' && 'YouTube'}
              </label>
            ))}
          </div>

          {/* Own Video */}
          {videoType === 'own' && (
            <div className="space-y-3">
              {/* Upload area or preview */}
              {videoBlobName ? (
                <div className="flex items-start gap-4 bg-surface-card border border-theme-border rounded-xl p-4">
                  {videoThumbnailUrl ? (
                    <img
                      src={videoThumbnailUrl}
                      alt="Thumbnail"
                      className="w-32 h-20 object-cover rounded-lg bg-gray-800"
                    />
                  ) : (
                    <div className="w-32 h-20 bg-gray-800 rounded-lg flex items-center justify-center">
                      <span className="material-symbols-outlined text-content-muted text-3xl">videocam</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{videoFilename}</p>
                    <p className="text-xs text-content-muted mt-0.5">{formatFileSize(videoSizeBytes)}</p>
                    <button
                      onClick={handleRemoveVideo}
                      className="mt-2 text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                    >
                      <span className="material-symbols-outlined text-sm">delete</span>
                      Remover vídeo
                    </button>
                  </div>
                </div>
              ) : (
                <VideoUploadPanel
                  lessonId={lesson.id}
                  directEnabled={directUploadEnabled}
                  maxBytes={maxVideoBytes}
                  videoTitle={videoTitle}
                  onUploaded={handleVideoUploaded}
                  onBusyChange={setIsUploadingVideo}
                  onValidationError={(message) => showToast(message, 'error')}
                  onUploadError={(err, name) =>
                    showToast(videoUploadErrorMessage(name, err, maxVideoBytes), 'error')
                  }
                />
              )}

              {/* Video title */}
              <div>
                <label className="block text-sm font-medium mb-1">Título do vídeo (opcional)</label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                  placeholder="Ex: Introdução ao módulo"
                />
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium mb-1">Thumbnail (opcional)</label>
                <div className="flex items-center gap-3">
                  {videoThumbnailUrl && (
                    <img src={videoThumbnailUrl} alt="Thumbnail" className="w-20 h-12 object-cover rounded" />
                  )}
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-theme-border text-content-muted hover:text-accent hover:border-accent/40 transition-colors cursor-pointer text-sm">
                    <span className="material-symbols-outlined text-lg">image</span>
                    {isUploadingThumbnail ? 'Enviando...' : 'Selecionar imagem'}
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleThumbnailUpload}
                      disabled={isUploadingThumbnail}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* YouTube */}
          {videoType === 'youtube' && (
            <div>
              <label className="block text-sm font-medium mb-1">YouTube URL</label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => handleYoutubeChange(e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl bg-surface-card border focus:outline-none ${
                  youtubeError ? 'border-red-500' : 'border-theme-border focus:border-accent'
                }`}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              {youtubeError && <p className="text-red-400 text-sm mt-1">{youtubeError}</p>}
              {embedUrl && (
                <div className="mt-3 aspect-video max-w-md rounded-xl overflow-hidden">
                  <iframe
                    src={embedUrl}
                    className="w-full h-full"
                    allowFullScreen
                    title="YouTube preview"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-medium mb-2">Arquivos</label>
          {attachments.length > 0 && (
            <div className="space-y-2 mb-3">
              {attachments.map((att, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-surface-card border border-theme-border rounded-lg px-4 py-2">
                  <span className="material-symbols-outlined text-content-muted">attach_file</span>
                  <span className="flex-1 truncate text-sm">{att.name}</span>
                  <span className="text-content-muted text-xs">
                    {(att.size_bytes / 1024).toFixed(0)} KB
                  </span>
                  <button
                    onClick={() => handleRemoveAttachment(idx)}
                    className="text-content-muted hover:text-red-400"
                  >
                    <span className="material-symbols-outlined text-lg">close</span>
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-theme-border text-content-muted hover:text-accent hover:border-accent/40 transition-colors cursor-pointer text-sm">
            <span className="material-symbols-outlined text-lg">upload_file</span>
            {isUploading ? 'Enviando...' : 'Upload de arquivo'}
            <input type="file" className="hidden" onChange={handleUpload} disabled={isUploading} />
          </label>
        </div>

        {/* Duration + Min Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Duração (min)</label>
            <input
              type="number"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Number(e.target.value))}
              onFocus={(e) => e.target.select()}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              min={0}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tempo mínimo (seg)</label>
            <input
              type="number"
              value={minTimeSeconds}
              onChange={(e) => setMinTimeSeconds(Number(e.target.value))}
              onFocus={(e) => e.target.select()}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              min={0}
            />
          </div>
        </div>

        {/* ================================================================= */}
        {/* Quiz Section */}
        {/* ================================================================= */}
        <div className="border-t border-theme-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Quiz (opcional)</h2>
            {!isLoadingQuiz && (
              <button
                type="button"
                onClick={() => {
                  setQuizEnabled(!quizEnabled);
                  if (!quizEnabled && questions.length === 0) {
                    setQuestions([emptyQuestion(0)]);
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  quizEnabled ? 'bg-accent' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    quizEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            )}
          </div>

          {isLoadingQuiz && (
            <p className="text-content-muted text-sm">Carregando quiz...</p>
          )}

          {!isLoadingQuiz && quizEnabled && (
            <div className="space-y-4">
              {/* Quiz settings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título do Quiz</label>
                  <input
                    type="text"
                    value={quizTitle}
                    onChange={(e) => setQuizTitle(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                    placeholder="Ex: Quiz - Análise de Crédito"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mínimo de acertos</label>
                  <input
                    type="number"
                    value={minCorrectAnswers}
                    onChange={(e) => setMinCorrectAnswers(Number(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                    min={1}
                    max={questions.length || 1}
                  />
                </div>
              </div>

              {/* Questions */}
              {questions.map((question, qIdx) => (
                <div
                  key={qIdx}
                  className="bg-surface-card border border-theme-border rounded-xl p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-accent">Pergunta {qIdx + 1}</span>
                    <button
                      onClick={() => removeQuestion(qIdx)}
                      className="text-content-muted hover:text-red-400 text-sm"
                    >
                      <span className="material-symbols-outlined text-lg">delete</span>
                    </button>
                  </div>

                  <input
                    type="text"
                    value={question.text}
                    onChange={(e) => updateQuestionText(qIdx, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-surface-primary border border-theme-border focus:border-accent focus:outline-none text-sm"
                    placeholder="Texto da pergunta"
                  />

                  <input
                    type="text"
                    value={question.explanation}
                    onChange={(e) => updateQuestionExplanation(qIdx, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-surface-primary border border-theme-border focus:border-accent focus:outline-none text-sm"
                    placeholder="Explicação (exibida após resposta, opcional)"
                  />

                  {/* Answers */}
                  <div className="space-y-2">
                    {question.answers.map((answer, aIdx) => (
                      <div key={aIdx} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCorrectAnswer(qIdx, aIdx)}
                          className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            answer.is_correct
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-500 hover:border-green-400'
                          }`}
                          title="Marcar como correta"
                        >
                          {answer.is_correct && (
                            <span className="material-symbols-outlined text-white text-xs">check</span>
                          )}
                        </button>
                        <input
                          type="text"
                          value={answer.text}
                          onChange={(e) => updateAnswerText(qIdx, aIdx, e.target.value)}
                          className="flex-1 px-3 py-1.5 rounded-lg bg-surface-primary border border-theme-border focus:border-accent focus:outline-none text-sm"
                          placeholder={`Alternativa ${String.fromCharCode(65 + aIdx)}`}
                        />
                        {question.answers.length > 2 && (
                          <button
                            onClick={() => removeAnswer(qIdx, aIdx)}
                            className="text-content-muted hover:text-red-400"
                          >
                            <span className="material-symbols-outlined text-lg">close</span>
                          </button>
                        )}
                      </div>
                    ))}
                    {question.answers.length < 6 && (
                      <button
                        onClick={() => addAnswer(qIdx)}
                        className="text-xs text-accent hover:text-accent-hover font-medium"
                      >
                        + Adicionar alternativa
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                onClick={addQuestion}
                className="w-full border-2 border-dashed border-theme-border rounded-xl p-3 text-content-muted hover:text-accent hover:border-accent/40 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <span className="material-symbols-outlined text-lg">add</span>
                Adicionar Pergunta
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isSaving || isUploadingVideo || isUploadingThumbnail}
            title={isUploadingVideo ? 'Aguarde o envio do vídeo terminar' : undefined}
            className="bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Salvando...' : isUploadingVideo ? 'Enviando vídeo...' : 'Salvar Página'}
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-2.5 rounded-xl font-medium border border-theme-border text-content-muted hover:text-content-primary transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLessonEditor;
